import {
  ApplePayForm,
  BoletoForm,
  CreditCardForm,
  GooglePayForm,
  NotFoundPaymentForm,
  NubankPayForm,
  PicPayForm,
  PixForm,
  PixAutoForm
} from '@/components/checkout/Payments';
import {
  RECAPTCHA_ERROR_MESSAGE,
  RECAPTCHA_VALIDATION_ERROR,
  UNKNOWN_ERROR
} from '@/constants/errors';
import { PaymentApprovedStatus } from '@/constants/payment';
import { useFacebookPixels } from '@/hooks/useFacebookPixels';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import useKwaiPixels from '@/hooks/useKwaiPixels';
import useTikTokPixels from '@/hooks/useTikTokPixels';
import AlertIcon from '@/icons/AlertIcon';
import ApplePayIcon from '@/icons/ApplePayIcon';
import BarcodeIcon from '@/icons/BarcodeIcon';
import GooglePayIcon from '@/icons/GooglePayIcon';
import NubankIcon from '@/icons/NubankIcon';
import PicPayIcon from '@/icons/PicPayIcon';
import PixIcon from '@/icons/PixIcon';
import PixAutoIcon from '@/icons/PixAutoIcon';
import {
  generateFingerprint,
  getCheckoutData,
  getPaymentStatus,
  startPayment,
  validateCouponService
} from '@/services/checkout';
import {
  CouponData,
  ErrorDetails,
  Order,
  Payment,
  PaymentMethod,
  PaymentMethodOption,
  PaymentPayload,
  PaymentTab,
  ProductData
} from '@/types';
import { trackCheckoutCompleted, trackCheckoutStarted, trackFormError, trackPaymentSelected } from '@/utils/analytics';
import { canPaymentMethodBePaidInInstallments } from '@/utils/payment';
import { CreditCardIcon } from '@heroicons/react/20/solid';
import moment from 'moment';
import { PropsWithChildren, createContext, useCallback, useMemo, useState } from 'react';
import { MutationStatus, useMutation, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';

type CheckoutContextData = {
  isFetching: boolean;
  offer?: ProductData;
  pay: (payload: PaymentPayload) => Promise<unknown>;
  setFirstPayment: (payload: Partial<Payment>) => void;
  setPayments: React.Dispatch<React.SetStateAction<Payment[]>>;
  checkPayment: () => Promise<unknown>;
  checkingPayment: boolean;
  paying: boolean;
  payments: Payment[];
  firstPayment: Payment | null;
  error: unknown;
  method: PaymentMethod | null;
  status: MutationStatus;
  creditCardError: string;
  setCreditCardError: (error: string) => void;
  setCountdownHeight: (height: number) => void;
  countdownHeight: number;
  // modalErrorMessage: string,
  // setModalErrorMessage: (message: string) => void,
  calcInstallments: any;
  setCalcInstallments: (installments: any) => void;
  fingerprint?: string;
  couponData?: CouponData;
  validateCoupon: (coupon: string) => Promise<unknown>;
  isLoadingValidateCoupon: boolean;
  applyCouponValue: number;
  setApplyCouponValue: (number: number) => void;
  paymentMethod: PaymentMethodOption | null;
  setPaymentMethod: (payment: PaymentMethodOption) => void;
  paymentTabs: PaymentTab[];
  hasAnySubscription: boolean;
  canBePaidInInstallments: boolean;
};

const CheckoutContext = createContext<CheckoutContextData>({} as CheckoutContextData);

const CheckoutProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const affiliateShortId = searchParams.get('affiliate') || undefined;
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption | null>(null);

  const [method, setMethod] = useState<PaymentMethod | null>(null);

  const [payments, setPayments] = useState<Payment[]>([]);

  const [firstPayment] = payments || [];

  const [creditCardError, setCreditCardError] = useState('');

  const [calcInstallments, setCalcInstallments] = useState();

  const [countdownHeight, setCountdownHeight] = useState(0);

  const [applyCouponValue, setApplyCouponValue] = useState(0);

  // const [modalErrorMessage, setModalErrorMessage] = useState('')

  const { data: offer, isLoading: isFetching } = useQuery<ProductData>({
    queryKey: ['checkout', id],
    queryFn: async () => {
      try {
        const data = await getCheckoutData(id as string, affiliateShortId as string);

        const defaultPaymentsOrder = data?.product?.paymentMethods || [];

        const allowedPaymentsMethods =
          data?.type === 'subscription'
            ? data?.product?.paymentMethods?.map(({ type }) => type) || ['pix', 'boleto', 'credit_card']
            : defaultPaymentsOrder?.map(({ type }) => type);

        const allPaymentsOrder = data?.product?.paymentsOrder?.length
          ? data?.product?.paymentsOrder
          : defaultPaymentsOrder?.map(({ type }: any) => type);

        let paymentsOrder = allPaymentsOrder?.filter((payment: any) =>
          allowedPaymentsMethods.includes(payment)
        );

        // Usar apenas os métodos configurados no produto
        let paymentMethods = data?.product?.paymentMethods?.filter((payment) =>
          allowedPaymentsMethods.includes(payment.type)
        );

        if (data.product?.image) {
          data.product.image = data.product?.image + '?width=180';
        }

        if (data.product?.bumps?.length > 0) {
          data.product.bumps = data?.product?.bumps?.map((bump) => {
            if (bump?.image) {
              bump.image = bump?.image + '?width=180';
            }
            return bump;
          });
        }

        return {
          ...data,
          product: {
            ...data.product,
            paymentMethods,
            paymentsOrder
          }
        };
      } catch (error) {
        return {} as ProductData;
      }
    },
    staleTime: 0, // Sempre considerar os dados como obsoletos para garantir dados frescos
    cacheTime: 5 * 60 * 1000, // Cache por 5 minutos
    refetchOnWindowFocus: true, // Recarregar quando a janela ganhar foco
    refetchOnMount: true, // Recarregar quando o componente for montado
    select(data) {
      const successPaymentsIds = firstPayment?.success?.map(({ id }) => id);

      const refusedPaymentsIds = firstPayment?.errors?.map(({ id }) => id);

      const { paymentsOrder } = data.product;

      const isApplePayAvailable =
        window.location.hostname === 'localhost' ||
        (!!window.ApplePaySession && ApplePaySession.canMakePayments());
      const isGooglePayAvailable = window.google?.payments?.api?.PaymentsClient;

            const filteredPaymentMethods = data?.product?.paymentMethods?.filter((method) => {
        // Verificar disponibilidade de Apple Pay e Google Pay
        if (method.type === 'applepay') return isApplePayAvailable;
        if (method.type === 'googlepay') return isGooglePayAvailable;

        return true;
      });

      return {
        ...data,
        paid: successPaymentsIds?.includes(data.id),
        isCreditCardPayment: true,
        product: {
          ...data.product,
          paymentMethods: filteredPaymentMethods?.sort(
            (a, b) => paymentsOrder.indexOf(a.type) - paymentsOrder.indexOf(b.type)
          ),
          bumps: data.product.bumps.filter(({ offer }) =>
            refusedPaymentsIds?.length ? refusedPaymentsIds?.includes(offer.id) : true
          )
        }
      };
    },
    onSuccess: (data) => {
      if (data?.id && data?.product) {
        trackCheckoutStarted(
          data.id,
          data.product.price,
          [{
            id: data.product.short_id,
            name: data.product.name,
            price: data.product.price,
            quantity: 1
          }]
        );
      }
    }
  });

  const googleAds = useGoogleAds();

  const facebookPixels = useFacebookPixels();

  const tikTokPixels = useTikTokPixels();

  const kwaiPixels = useKwaiPixels();

  const {
    mutateAsync: pay,
    isLoading: paying,
    error,
    status
  } = useMutation({
    mutationFn: async (payload: PaymentPayload) => {
      const refusedPaymentsIds = firstPayment?.errors?.map(({ id }) => id);
      const newPayload = {
        ...payload,
        items: payload.items.filter(({ id }) =>
          refusedPaymentsIds?.length ? refusedPaymentsIds?.includes(id) : true
        )
      };

      if (payload.paymentMethod) {
        trackPaymentSelected(payload.paymentMethod);
      }

      return startPayment(offer?.product.short_id as string, {
        ...newPayload,
        checkoutUrl: window.location.href
      });
    },
    onMutate: (payload) => {
      setCreditCardError('');
      setMethod(payload.paymentMethod);
    },
            onSuccess: ({ payments, refreshToken, accessToken }) => {
      // Preservar o paymentMethod correto baseado no método selecionado
      const firstPayment = payments[0];
      const preservedPaymentMethod = method === 'pix_auto' ? 'pix_auto' : firstPayment?.paymentMethod;

      setFirstPayment({
        accessToken,
        refreshToken,
        paymentMethod: preservedPaymentMethod
      });
      setPayments(payments);

      payments.forEach((payment: Payment) => {
        const method = payment?.orders?.[0]?.paymentMethod;
        const status = payment?.orders?.[0]?.status;

        const ticketGenerated = method === 'boleto' && status === 'waiting_payment';
        const pixGenerated = (method === 'pix' || method === 'pix_auto') && status === 'waiting_payment';
        const picpayGenerated = method === 'picpay' && status === 'waiting_payment';
        const nubankGenerated = method === 'openfinance_nubank' && status === 'waiting_payment';

        const paymentApproved = PaymentApprovedStatus.includes(status);

        const amount: number = (payment?.orders || []).reduce((acc: number, order: Order) => acc + Number(order?.amount || 0), 0);

        if (ticketGenerated) {
          googleAds.onTicketGenerated(payment);
          facebookPixels.onTicketGenerated(payment);
          tikTokPixels.onTicketGenerated(payment);
          kwaiPixels.onTicketGenerated(payment);
        }

        if (pixGenerated) {
          googleAds.onPixGenerated(payment);
          facebookPixels.onPixGenerated(payment);
          tikTokPixels.onPixGenerated(payment);
          kwaiPixels.onPixGenerated(payment);
        }

        if (picpayGenerated) {
          facebookPixels.onPicpayGenerated(payment);
          tikTokPixels.onPicpayGenerated(payment);
          kwaiPixels.onPicpayGenerated(payment);
        }

        if (nubankGenerated) {
          facebookPixels.onNubankGenerated(payment);
          tikTokPixels.onNubankGenerated(payment);
          kwaiPixels.onNubankGenerated(payment);
        }

        if (paymentApproved) {
          facebookPixels.onPaymentApproved(payment, amount);
          tikTokPixels.onPaymentApproved(payment, amount);
          kwaiPixels.onPaymentApproved(payment, amount);
          trackCheckoutCompleted(payment?.orders[0]?.id, payment?.orders[0]?.amount, method);
        }
      });
    },
    onError: (error: ErrorDetails) => {
      if (error?.response?.data?.detail === RECAPTCHA_VALIDATION_ERROR) {
        setCreditCardError(RECAPTCHA_ERROR_MESSAGE);
        trackFormError('checkout', 'recaptcha', RECAPTCHA_ERROR_MESSAGE);
      } else {
        const errorMessage = error?.response?.data?.detail || UNKNOWN_ERROR;
        setCreditCardError(errorMessage);
        trackFormError('checkout', 'payment', errorMessage);
      }
    }
    // retry: (count, error) => {
    //   if (count < 1 && error?.response?.data?.detail === RECAPTCHA_VALIDATION_ERROR) {
    //     return true
    //   }
    //   return false
    // }
  });

  const setFirstPayment = useCallback((payload: Partial<Payment>) => {
    setPayments(([firstPayment, ...prevPayments]) => [
      { ...firstPayment, ...payload },
      ...prevPayments
    ]);
  }, []);

  const { mutateAsync: checkPayment, isLoading: checkingPayment } = useMutation({
    mutationFn: () => getPaymentStatus(firstPayment!.id),
    onSuccess({ status, refreshToken, accessToken }) {
      // Preservar o paymentMethod original ao atualizar o status
      const currentPaymentMethod = firstPayment?.paymentMethod;
      setFirstPayment({
        status,
        refreshToken,
        accessToken,
        paymentMethod: currentPaymentMethod
      });
    }
  });

  const { data: fingerprint } = useQuery({
    queryFn: () => generateFingerprint()
  });

  const {
    data: couponData,
    mutateAsync: validateCoupon,
    isLoading: isLoadingValidateCoupon
  } = useMutation<CouponData, Error, string>({
    mutationFn: (coupon: string) =>
      validateCouponService({ code: coupon.toLowerCase(), offerId: id?.split('_')[0] }),
    onSuccess(data: any) {
      const { startTime, endTime } = data;
      const now = moment();
      const lifetimeDate = moment('9999-12-31');

      if (now.isBetween(moment(startTime), endTime ? moment(endTime) : lifetimeDate)) {
        setApplyCouponValue(1);
      } else {
        throw new Error('error');
      }
    }
  });

  const mapIcons: any = {
    credit_card: CreditCardIcon,
    boleto: BarcodeIcon,
    pix: PixIcon,
    pix_auto: PixAutoIcon,
    picpay: PicPayIcon,
    applepay: ApplePayIcon,
    googlepay: GooglePayIcon,
    openfinance_nubank: NubankIcon
  };

  const mapForms: any = {
    credit_card: CreditCardForm,
    boleto: BoletoForm,
    pix: PixForm,
    pix_auto: PixAutoForm,
    picpay: PicPayForm,
    applepay: ApplePayForm,
    googlepay: GooglePayForm,
    openfinance_nubank: NubankPayForm
  };

  const paymentTabs = useMemo(() => {
    const paymentMethods = offer?.product?.paymentMethods || [];

    // Para produtos de assinatura, usar apenas os métodos configurados no produto
    // Para produtos únicos, usar apenas os métodos configurados no produto
    let allowedPymentsMethods: PaymentMethod[] =
      offer?.type === 'subscription'
        ? paymentMethods?.map(({ type }) => type) || ['pix', 'boleto', 'credit_card']
        : paymentMethods?.map(({ type }) => type);

    // Para produtos únicos, remover pix_auto mesmo que venha no response
    if (offer?.type === 'unique') {
      allowedPymentsMethods = allowedPymentsMethods.filter(method => method !== 'pix_auto');
    }

    const paymentsOrder =
      offer?.product?.paymentsOrder?.filter((payment) => allowedPymentsMethods.includes(payment)) ||
      allowedPymentsMethods;

    return paymentMethods
      .filter(({ type }: any) => allowedPymentsMethods.includes(type) && type !== 'threeDs')
      ?.sort(
        (a: PaymentMethodOption, b: PaymentMethodOption) =>
          paymentsOrder.indexOf(a.type) - paymentsOrder.indexOf(b.type)
      )
      ?.map((item: PaymentMethodOption) => ({
        id: item.type,
        label: item.name,
        Icon: mapIcons[item.type] || AlertIcon,
        Component: mapForms[item.type] || NotFoundPaymentForm
      }));
  }, [offer]);

  const hasAnySubscription = useMemo(() => {
    if (!offer) {
      return false;
    }
    if (offer?.type === 'subscription') {
      return true;
    }
    return offer?.product?.bumps.some((bump) => bump.offer?.type === 'subscription');
  }, [offer]);

  const canBePaidInInstallments = useMemo(() => {
    if (!offer || !paymentMethod) {
      return false;
    }
    return canPaymentMethodBePaidInInstallments(paymentMethod.type);
  }, [offer, paymentMethod]);

  const value = useMemo(
    () => ({
      isFetching: isFetching,
      offer,
      pay,
      firstPayment,
      setFirstPayment,
      paying,
      checkPayment,
      setPayments,
      checkingPayment,
      payments,
      error,
      method,
      status,
      creditCardError,
      setCreditCardError,
      setCountdownHeight,
      countdownHeight,
      // modalErrorMessage,
      // setModalErrorMessage,
      calcInstallments,
      setCalcInstallments,
      fingerprint,
      couponData,
      validateCoupon,
      isLoadingValidateCoupon,
      applyCouponValue,
      setApplyCouponValue,
      paymentMethod,
      setPaymentMethod,
      paymentTabs: paymentTabs || [],
      canBePaidInInstallments,
      hasAnySubscription
    }),
    [
      isFetching,
      offer,
      pay,
      firstPayment,
      setFirstPayment,
      paying,
      checkPayment,
      checkingPayment,
      payments,
      error,
      method,
      status,
      creditCardError,
      setCreditCardError,
      setCountdownHeight,
      countdownHeight,
      // modalErrorMessage,
      // setModalErrorMessage,
      calcInstallments,
      setCalcInstallments,
      fingerprint,
      couponData,
      validateCoupon,
      isLoadingValidateCoupon,
      applyCouponValue,
      setApplyCouponValue,
      paymentMethod,
      setPaymentMethod,
      paymentTabs,
      canBePaidInInstallments,
      hasAnySubscription
    ]
  );

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>;
};

export { CheckoutContext, CheckoutProvider };
